import { FormCol4 } from 'motion-components';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaQuestionCircle,
} from 'react-icons/fa';

export type AiExtractionStatus = 'match' | 'minor_deviation' | 'major_deviation' | 'not_found';

export type AiExtractionDetails = {
  status?: string;
  extractedValue?: string;
  extractedUnit?: string;
  extractedFile?: string;
  extractedLocation?: string;
  extractedSnippet?: string;
  userValue?: string;
  userUnit?: string;
  isAiCorrected?: boolean;
};

function formatExtractionTooltip(d: AiExtractionDetails): React.ReactNode {
  const rows: Array<{ label: string; value: string }> = [];
  if (d.userValue != null && d.userValue !== '')
    rows.push({ label: 'Τιμή χρήστη', value: d.userValue });
  if (d.extractedValue != null && d.extractedValue !== '')
    rows.push({ label: 'Εξαγόμενη Τιμή', value: d.extractedValue });
  if (d.extractedFile) rows.push({ label: 'Από Αρχείο', value: d.extractedFile });
  if (d.extractedLocation) rows.push({ label: 'Τοποθεσία', value: d.extractedLocation });
  if (d.extractedSnippet) rows.push({ label: 'Απόσπασμα', value: d.extractedSnippet });
  if (d.extractedUnit != null && d.extractedUnit !== '')
    rows.push({ label: 'Μονάδα (εξαγ.)', value: d.extractedUnit });
  if (d.userUnit != null && d.userUnit !== '')
    rows.push({ label: 'Μονάδα (χρήστη)', value: d.userUnit });
  if (rows.length === 0) return null;
  return (
    <div className="text-start small">
      {rows.map(({ label, value }) => (
        <div key={label}>
          <strong>{label}:</strong> {value}
        </div>
      ))}
    </div>
  );
}

const STATUS_ICONS: Record<AiExtractionStatus, React.ComponentType<{ className?: string; size?: number }>> = {
  match: FaCheckCircle,
  minor_deviation: FaExclamationTriangle,
  major_deviation: FaTimesCircle,
  not_found: FaQuestionCircle,
};

const STATUS_CONFIG: Record<
  AiExtractionStatus,
  { label: string; className?: string }
> = {
  match: {
    label: 'Match (πράσινο)',
    className: 'text-success',
  },
  minor_deviation: {
    label: 'Μικρή απόκλιση / πιθανή στρογγυλοποίηση',
    className: 'text-warning',
  },
  major_deviation: {
    label: 'Σημαντική απόκλιση',
    className: 'text-danger',
  },
  not_found: {
    label: 'Δεν βρέθηκε / χαμηλή βεβαιότητα',
    className: 'text-secondary',
  },
};

type AiExtractionStatusIconProps = {
  status: AiExtractionStatus | string | undefined;
  showLabel?: boolean;
  className?: string;
  extractionDetails?: AiExtractionDetails | null;
};

/**
 * Displays an icon based on AI extraction status.
 * Οπτική γλώσσα: Match (πράσινο), Μικρή/Σημαντική απόκλιση, Δεν βρέθηκε.
 * On hover, shows extraction details (extractedValue, userValue, file, location, snippet, etc.) when extractionDetails is provided.
 */
export const AiExtractionStatusIcon: React.FC<AiExtractionStatusIconProps> = ({
  status,
  showLabel = false,
  className = '',
  extractionDetails,
}) => {
  if (status == null || status === '') return null;
  const key = status as AiExtractionStatus;
  const config = STATUS_CONFIG[key];
  if (!config) return null;

  const IconComponent = STATUS_ICONS[key];
  const iconEl = (
    <span
      className={`inline-flex items-center gap-1.5 ${config.className ?? ''} ${className}`.trim()}
      title={extractionDetails ? undefined : config.label}
      role="img"
      aria-label={config.label}
    >
      <IconComponent className={config.className} size={14} />
      {showLabel && <span className="text-sm font-medium">{config.label}</span>}
    </span>
  );

  const tooltipContent = extractionDetails && formatExtractionTooltip(extractionDetails);
  if (tooltipContent) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="ai-extraction-details">
            <div className="mb-1 fw-semibold">{config.label}</div>
            {tooltipContent}
          </Tooltip>
        }
      >
        <span style={{ cursor: 'help' }}>{iconEl}</span>
      </OverlayTrigger>
    );
  }

  return <span title={config.label}>{iconEl}</span>;
};

type AiExtractionDecorationProps = {
  aiFileDataExtractionObject?: Record<string, any> | null;
  className?: string;
  children?: React.ReactNode;
  as?: React.ComponentType<any>;
};

/**
 * Scaffolds a visual indicator or decoration for extracted AI file data.
 * Displays a badge if key extracted values are available (e.g. manufacturer, chemistry).
 * Renders children components below the extraction decoration.
 */
export const AiExtractionDecoration: React.FC<AiExtractionDecorationProps> = ({
  aiFileDataExtractionObject,
  className = '',
  children,
  as = FormCol4,
}) => {
  const Wrapper = as;
  const status = aiFileDataExtractionObject?.status as AiExtractionStatus | undefined;
  return (
    <Wrapper>
      {children && (
        <div
          className="position-relative overflow-visible"
          style={status ? { paddingRight: '1.75rem' } : undefined}
        >
          {children}
          {status && (
            <span
              className="position-absolute bottom-0 mb-1"
              style={{ right: '0.5rem', lineHeight: 1 }}
            >
              <AiExtractionStatusIcon
                status={status}
                showLabel={false}
                extractionDetails={aiFileDataExtractionObject as AiExtractionDetails}
              />
            </span>
          )}
        </div>
      )}
    </Wrapper>
  );
};

export default AiExtractionDecoration;
